// Portal — list current user's print orders (status, tracking, receipts).
// Polls /api/portal/print-orders with auth.
// i18n : dictionnaire inline COPY (FR/EN/ES/PT).
import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Loader2, Package, ArrowRight, Truck, ExternalLink, CheckCircle2, Clock } from "lucide-react";
import Layout from "@/components/Layout";
import SEO from "@/components/SEO";
import { useAuth } from "@/lib/auth";
import { apiFetch } from "@/lib/api";
import { useLang } from "@/lib/i18n";

const AUTH_KEY = "geovinum.auth.v1";

function authHeaders() {
  try {
    const raw = localStorage.getItem(AUTH_KEY);
    const parsed = raw ? JSON.parse(raw) : null;
    const t = parsed?.token;
    return t ? { Authorization: `Bearer ${t}` } : {};
  } catch {
    return {};
  }
}

const CURRENCY_LOCALE = { fr: "fr-FR", en: "en-GB", es: "es-ES", pt: "pt-PT" };

const STATUS_META = {
  fr: {
    initiated: { label: "Initiée", color: "#9ba3ad", Icon: Clock },
    created: { label: "Reçue", color: "#2e5067", Icon: CheckCircle2 },
    printed: { label: "Imprimée", color: "#7a4dad", Icon: CheckCircle2 },
    shipped: { label: "Expédiée", color: "#ce1a5f", Icon: Truck },
    delivered: { label: "Livrée", color: "#2e9657", Icon: CheckCircle2 },
    canceled: { label: "Annulée", color: "#ad4d4d", Icon: Clock },
  },
  en: {
    initiated: { label: "Initiated", color: "#9ba3ad", Icon: Clock },
    created: { label: "Received", color: "#2e5067", Icon: CheckCircle2 },
    printed: { label: "Printed", color: "#7a4dad", Icon: CheckCircle2 },
    shipped: { label: "Shipped", color: "#ce1a5f", Icon: Truck },
    delivered: { label: "Delivered", color: "#2e9657", Icon: CheckCircle2 },
    canceled: { label: "Canceled", color: "#ad4d4d", Icon: Clock },
  },
  es: {
    initiated: { label: "Iniciada", color: "#9ba3ad", Icon: Clock },
    created: { label: "Recibida", color: "#2e5067", Icon: CheckCircle2 },
    printed: { label: "Impresa", color: "#7a4dad", Icon: CheckCircle2 },
    shipped: { label: "Enviada", color: "#ce1a5f", Icon: Truck },
    delivered: { label: "Entregada", color: "#2e9657", Icon: CheckCircle2 },
    canceled: { label: "Cancelada", color: "#ad4d4d", Icon: Clock },
  },
  pt: {
    initiated: { label: "Iniciada", color: "#9ba3ad", Icon: Clock },
    created: { label: "Recebida", color: "#2e5067", Icon: CheckCircle2 },
    printed: { label: "Impressa", color: "#7a4dad", Icon: CheckCircle2 },
    shipped: { label: "Enviada", color: "#ce1a5f", Icon: Truck },
    delivered: { label: "Entregue", color: "#2e9657", Icon: CheckCircle2 },
    canceled: { label: "Cancelada", color: "#ad4d4d", Icon: Clock },
  },
};

const COPY = {
  fr: {
    seo_title: "Mes commandes — geoVINUM",
    seo_desc: "Suivez vos commandes posters et abonnements.",
    err_generic: "Erreur",
    back_billing: "Retour au compte",
    pill: "Mes commandes",
    h1: "Vos commandes posters",
    sub: "Suivez la production, l'expédition et la livraison de vos cartes.",
    empty_h: "Vous n'avez pas encore de commande poster.",
    empty_p: "Commandez votre première carte imprimée depuis nos pages produits.",
    empty_cta: "geoVINUM Atlas",
    ordered_on: "Commandée le",
    academy_discount: "· Academy -10%",
    gelato_id: "Gelato ID",
    tracking_label: "Suivi",
    tracking_default: "Voir le suivi",
  },
  en: {
    seo_title: "My orders — geoVINUM",
    seo_desc: "Track your poster orders and subscriptions.",
    err_generic: "Error",
    back_billing: "Back to account",
    pill: "My orders",
    h1: "Your poster orders",
    sub: "Track the production, shipping and delivery of your maps.",
    empty_h: "You don't have any poster order yet.",
    empty_p: "Order your first printed map from our product pages.",
    empty_cta: "geoVINUM Atlas",
    ordered_on: "Ordered on",
    academy_discount: "· Academy -10%",
    gelato_id: "Gelato ID",
    tracking_label: "Tracking",
    tracking_default: "View tracking",
  },
  es: {
    seo_title: "Mis pedidos — geoVINUM",
    seo_desc: "Sigue tus pedidos de pósters y suscripciones.",
    err_generic: "Error",
    back_billing: "Volver a la cuenta",
    pill: "Mis pedidos",
    h1: "Tus pedidos de pósters",
    sub: "Sigue la producción, el envío y la entrega de tus mapas.",
    empty_h: "Aún no tienes ningún pedido de póster.",
    empty_p: "Pide tu primer mapa impreso desde nuestras páginas de producto.",
    empty_cta: "geoVINUM Atlas",
    ordered_on: "Pedido el",
    academy_discount: "· Academy -10%",
    gelato_id: "Gelato ID",
    tracking_label: "Seguimiento",
    tracking_default: "Ver seguimiento",
  },
  pt: {
    seo_title: "As minhas encomendas — geoVINUM",
    seo_desc: "Acompanhe as suas encomendas de posters e subscrições.",
    err_generic: "Erro",
    back_billing: "Voltar à conta",
    pill: "As minhas encomendas",
    h1: "As suas encomendas de posters",
    sub: "Acompanhe a produção, o envio e a entrega dos seus mapas.",
    empty_h: "Ainda não tem nenhuma encomenda de poster.",
    empty_p: "Encomende o seu primeiro mapa impresso a partir das nossas páginas de produto.",
    empty_cta: "geoVINUM Atlas",
    ordered_on: "Encomendado a",
    academy_discount: "· Academy -10%",
    gelato_id: "Gelato ID",
    tracking_label: "Rastreio",
    tracking_default: "Ver rastreio",
  },
};

const PortalOrdersPage = () => {
  const navigate = useNavigate();
  const { user, ready } = useAuth();
  const lang = useLang();
  const c = COPY[lang] || COPY.fr;
  const statusMeta = STATUS_META[lang] || STATUS_META.fr;
  const locale = CURRENCY_LOCALE[lang] || "fr-FR";
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const fmtEUR = (cents) =>
    cents != null
      ? new Intl.NumberFormat(locale, { style: "currency", currency: "EUR" }).format(cents / 100)
      : "—";

  const fmtDate = (iso) => {
    if (!iso) return "—";
    try {
      return new Intl.DateTimeFormat(locale, { dateStyle: "medium" }).format(new Date(iso));
    } catch {
      return iso;
    }
  };

  useEffect(() => {
    if (!ready) return;
    if (!user) {
      navigate("/login?next=/portal/orders", { replace: true });
      return;
    }
    (async () => {
      const { ok, data, error: err } = await apiFetch(`/api/portal/print-orders`, { headers: authHeaders() });
      if (!ok) setError(err || c.err_generic);
      else setOrders(data.items || []);
      setLoading(false);
    })();
  }, [ready, user, navigate, c.err_generic]);

  return (
    <Layout testId="page-portal-orders">
      <SEO title={c.seo_title} description={c.seo_desc} />

      <section className="bg-[#fafaf7] py-16 md:py-24 min-h-[70vh]">
        <div className="max-w-5xl mx-auto px-6 lg:px-10">
          <Link to="/portal/billing" className="inline-flex items-center gap-1.5 text-[13px] text-[color:var(--gv-ink)]/60 hover:text-[color:var(--gv-blue)] mb-6 transition-colors" data-testid="orders-back-billing">
            <ArrowRight size={14} className="rotate-180" /> {c.back_billing}
          </Link>

          <header className="mb-10">
            <div className="inline-flex items-center gap-2 bg-white border border-[color:var(--gv-line)] text-[color:var(--gv-blue)] px-3 py-1.5 rounded-full text-[10.5px] uppercase tracking-[0.15em] font-medium mb-4 shadow-sm">
              <Package size={11} /> {c.pill}
            </div>
            <h1 className="font-display text-4xl sm:text-5xl text-[color:var(--gv-ink)]">{c.h1}</h1>
            <p className="text-[color:var(--gv-ink)]/65 mt-2">{c.sub}</p>
          </header>

          {loading && (
            <div className="py-16 text-center">
              <Loader2 size={24} className="animate-spin mx-auto text-[color:var(--gv-blue)]" />
            </div>
          )}

          {!loading && error && (
            <div className="bg-red-50 border border-red-200 text-red-700 text-[13px] px-4 py-3 rounded-[5px]">{error}</div>
          )}

          {!loading && !error && orders.length === 0 && (
            <div className="bg-white border border-dashed border-[color:var(--gv-line)] rounded-[10px] py-16 text-center" data-testid="portal-orders-empty">
              <Package size={28} className="mx-auto text-[color:var(--gv-blue)]/40 mb-4" />
              <div className="font-display text-xl text-[color:var(--gv-ink)] mb-2">{c.empty_h}</div>
              <p className="text-[13px] text-[color:var(--gv-ink)]/60 max-w-md mx-auto mb-5">
                {c.empty_p}
              </p>
              <div className="flex items-center justify-center gap-3">
                <Link to="/products/atlas-magnum" className="btn-primary">{c.empty_cta} <ArrowRight size={14} /></Link>
              </div>
            </div>
          )}

          {!loading && orders.length > 0 && (
            <ul className="space-y-4">
              {orders.map((o) => {
                const meta = statusMeta[o.status] || { label: o.status || "—", color: "#9ba3ad", Icon: Clock };
                return (
                  <li
                    key={o.session_id}
                    className="bg-white border border-[color:var(--gv-line)] rounded-[10px] p-5 md:p-6 shadow-sm"
                    data-testid={`portal-order-${o.session_id}`}
                  >
                    <div className="flex items-start justify-between gap-4 flex-wrap">
                      <div className="min-w-0">
                        <div className="font-display text-xl text-[color:var(--gv-ink)] mb-1">
                          {o.slug === "atlas-magnum" || o.slug === "atlas-pro" ? "geoVINUM Atlas" : o.slug} — {o.size_id}
                        </div>
                        <div className="text-[12px] text-[color:var(--gv-ink)]/55 mb-2">
                          {c.ordered_on} {fmtDate(o.created_at)} · {fmtEUR(o.amount_cents)}
                          {o.is_academy_member && <span className="ml-1 text-[color:var(--gv-pink)]"> {c.academy_discount}</span>}
                        </div>
                        {o.gelato_order_id && (
                          <div className="text-[11.5px] text-[color:var(--gv-ink)]/45 font-mono">{c.gelato_id}&nbsp;: {o.gelato_order_id}</div>
                        )}
                      </div>
                      <div
                        className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-[11px] uppercase tracking-widest font-medium"
                        style={{ background: `${meta.color}15`, color: meta.color }}
                        data-testid={`order-status-${o.session_id}`}
                      >
                        <meta.Icon size={12} /> {meta.label}
                      </div>
                    </div>

                    {(o.tracking_code || o.tracking_url) && (
                      <div className="mt-4 pt-4 border-t border-[color:var(--gv-line)] flex items-center gap-3 flex-wrap">
                        <Truck size={14} className="text-[color:var(--gv-blue)]" />
                        <span className="text-[13px] text-[color:var(--gv-ink)]/70">
                          {o.carrier ? `${o.carrier} — ` : ""}{c.tracking_label}&nbsp;:
                        </span>
                        {o.tracking_url ? (
                          <a
                            href={o.tracking_url}
                            target="_blank"
                            rel="noreferrer"
                            className="text-[13px] text-[color:var(--gv-pink)] hover:underline inline-flex items-center gap-1 font-mono"
                            data-testid={`order-tracking-${o.session_id}`}
                          >
                            {o.tracking_code || c.tracking_default} <ExternalLink size={11} />
                          </a>
                        ) : (
                          <span className="text-[13px] text-[color:var(--gv-ink)] font-mono">{o.tracking_code}</span>
                        )}
                      </div>
                    )}
                  </li>
                );
              })}
            </ul>
          )}
        </div>
      </section>
    </Layout>
  );
};

export default PortalOrdersPage;
